import FlightsSearchControlsGC, {
  navigate,
} from '@skyscanner-internal/global-components/flights-search-controls';
import {
  fetchCalendarPrices,
  fetchMonthPrices,
  fetchSuggestions,
} from '@skyscanner-internal/global-components/flights-search-controls/data';
import logger from 'saddlebag-logger';

import {
  buildFlightSearchUrl,
  buildHotelSearchUrl,
  hasDestination,
} from './helpers';

import type {
  InternalGCSearchParams,
  SearchParams,
} from '@skyscanner-internal/global-components/flights-search-controls/common/types/search';
import type { LocalisedURLStrings } from '@skyscanner-internal/global-components/flights-search-controls/utils/url-builder';
import type { FlightsSearchControlsGCWrapperProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightsSearchControlsGCWrapperProps';

const FlightsSearchControlsGCWrapper = ({
  cldr,
  culture,
  destination,
  deviceType,
  featureDecisions,
  initialSearchParams,
  origin,
  pageType,
  strings,
  utid,
}: FlightsSearchControlsGCWrapperProps) => {
  /**
   * Custom search handler that implements parallel search functionality.
   * When the user checks "Add a hotel", this opens flight search in a new tab
   * while navigating to hotel search in the current tab.
   *
   * Note: Parallel search is not supported for "Everywhere" searches, as hotels
   * require a specific destination.
   */
  const handleSearch = (
    searchParams: InternalGCSearchParams,
    searchStrings?: LocalisedURLStrings,
  ) => {
    const shouldAttemptParallelSearch =
      searchParams.shouldPerformParallelSearch;
    const hasValidDestination = hasDestination(
      searchParams.legs?.[0]?.destination,
    );

    // Parallel search only works with specific destinations, not "Everywhere"
    const canAttemptParallelSearch =
      shouldAttemptParallelSearch && hasValidDestination;

    if (!canAttemptParallelSearch) {
      navigate(searchParams, searchStrings);
      return;
    }

    const hotelUrl = buildHotelSearchUrl(searchParams);
    const flightUrl = searchStrings
      ? buildFlightSearchUrl(searchParams, searchStrings)
      : null;

    if (!flightUrl || !hotelUrl) {
      navigate(searchParams, searchStrings);
      return;
    }

    try {
      const newWindow = globalThis.open(
        flightUrl,
        '_blank',
        'noopener,noreferrer',
      );
      if (newWindow && 'opener' in newWindow) {
        newWindow.opener = null;
      }
    } catch (error) {
      logger.logError(
        error instanceof Error
          ? error
          : new Error('Failed to open flight search in new tab'),
        { component: 'FlightsSearchControlsGCWrapper' },
      );
    }

    // Delay navigation to avoid popup blocking by browsers.
    // 500ms delay determined by Banana's testing as the optimal wait time.
    // Reference: https://github.com/Skyscanner/banana/blob/main/libs/shared/banana/redux/src/helpers/navigationHandlers/useCombinedResultsSearch.ts#L247-L250
    setTimeout(() => {
      globalThis.location.assign(hotelUrl);
    }, 500);
  };

  // Some initial search state copied from Banana:
  // https://github.skyscannertools.net/skyscanner/banana/blob/d22fca58b19c1901debb202cf7095169efa37ee3/packages/webapp/src/state/user/search/initialState.ts#L7
  // with params passed from Falcon server for origin, destination, adults, dates, and trip type
  // which replicate previous behaviour found in the OC.

  // Building of this initial search state is very manual and replicating all previous OC and all
  // new Banana-based GC functionality would not be feasable. We will plan to work with Goose on
  // making building this more convenient, likely with helper functions exposed from the OC
  // to persist user settings where appropriate.

  const initialSearchState: SearchParams = {
    version: '1',
    adults: initialSearchParams?.adults ?? 1,
    child_ages: initialSearchParams?.child_ages ?? [],
    cabinClass: initialSearchParams?.cabinClass ?? 'ECONOMY',
    legs: [
      {
        origin,
        destination,
        date: initialSearchParams?.departureDate ?? '',
        return_date: initialSearchParams?.returnDate ?? undefined,
      },
    ],
    options: {
      // We have no controls for these currently, so default the options
      include_unpriced_itineraries: true,
      include_mixed_booking_options: true,
    },
    alternativeDestinations: false,
    alternativeOrigins: false,
    state: {},
    // prefer_directs was previously persisted by some local storage via the OC
    // but we don't have access to that in Falcon server, and don't want to replicate
    // client-side local storage in this component
    // https://github.skyscannertools.net/skyscanner/flights-search-controls/blob/9c3ade65042e4cfd4aa09380b7f7174083f8f92d/src/components/App/defaults.ts#L252-L253
    prefer_directs: false,
    tripType: initialSearchParams?.tripType ?? 'RETURN',
    // Controls whether the parallel search checkbox is checked by default.
    // When the feature is enabled, the checkbox should be checked by default.
    shouldPerformParallelSearch:
      featureDecisions.enableParallelSearchOption ?? false,
    fareAttributes: initialSearchParams?.fareAttributes ?? [],
  };

  const isProduction = process.env.NODE_ENV === 'production';

  return (
    <FlightsSearchControlsGC
      onSearch={handleSearch}
      cldr={cldr}
      culture={culture}
      strings={strings}
      fetchCalendarPrices={fetchCalendarPrices}
      fetchMonthPrices={fetchMonthPrices}
      fetchSuggestions={fetchSuggestions}
      initialSearchState={initialSearchState}
      logger={logger}
      featureDecisions={featureDecisions}
      trackingProps={{
        platform: deviceType,
        screenName: pageType,
      }}
      xSkyscannerClient="falcon"
      isProductionEnv={isProduction}
      utid={utid}
    />
  );
};

export default FlightsSearchControlsGCWrapper;
